import { data } from 'react-router-dom';
import api from './client';

export const BlishAPI = {
    auth: {
        /**
         * @param {string} email
         */
        checkEmail: (email) => api.post('/auth/check-email', { email }),
        /**
         * @param {Object} credentials
         */
        login: (credentials) => api.post('/auth/login', credentials),
        /**
         * @param {Object} data
         */
        register: (data) => api.post('/auth/register', data),
        /**
         * @param {{email:string,code:string}} data
         */
        verifyEmail: (data) => api.post('/auth/verify-email', data),
        getProfile: () => api.get('/auth/me'),
        /**
         * @param {Object} data
         */
        updateProfile: (data) => api.put('/auth/me', data),
        /**
         * @param {string} refreshToken
         */
        logout: (refreshToken) => api.post('/auth/logout', { refreshToken }),
        /**
         * @param {string} email
         */
        forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
        /**
         * @param {Object} data
         */
        resetPassword: (data) => api.post('/auth/reset-password', data),
    },

    wallet: {
        getWallet: () => api.get('/wallet'),
        getTransactions: (limit = 50, offset = 0) => api.get('/wallet/transactions', { params: { limit, offset } }),
        getPinStatus: () => api.get('/wallet/pin-status'),
        /**
         * @param {string} pin
         */
        setPin: (pin) => api.post('/wallet/set-pin', { pin }),
        requestPinReset: () => api.post('/wallet/forgot-pin'),
        /**
         * @param {{reset_code:string,new_pin:string}} data
         */
        resetPin: (data) => api.post('/wallet/reset-pin', data),

        // Withdrawals
        /**
         * @param {{amount:number,pin:string}} data
         * @param {string} idempotencyKey
         */
        withdrawStripe: (data, idempotencyKey) =>
            api.post('/wallet/withdraw', data, { headers: { 'X-Idempotency-Key': idempotencyKey } }),
        /**
         * @param {Object} data
         */
        withdrawPaystack: (data) => api.post('/wallet/paystack/withdraw', data),

        // Onboarding
        getStripeConnectStatus: () => api.get('/wallet/connect/status'),
        /**
         * @param {string} origin_url
         */
        createStripeOnboarding: (origin_url) => api.post('/wallet/connect/onboard', { origin_url }),
    },

    challenges: {
        submitReview:(id, data)=>api.post(`/reviews/${id}`, data),
        rematch:(id)=>api.post(`/challenges/${id}/rematch`, {},),
        myHistory:()=>api.get('/challenges/my-history',),
        /**
         * @param {Object} data
         */
        create: (data) => api.post('/challenges', data),
        /**
         * @param {string} [status]
         */
        list: (status) => api.get('/challenges', { params: { status } }),
        /**
         * @param {string} id
         */
        getDetails: (id) => api.get(`/challenges/${id}`),
        /**
         * @param {string} id
         */
        join: (id) => api.post(`/challenges/${id}/join`),
        /**
         * @param {string} id
         * @param {string} winnerId
         */
        declareResult: (id, winnerId) => api.post(`/challenges/${id}/declare-result`, { declared_winner_id: winnerId }),
        /**
         * @param {string} id
         * @param {boolean} accepted
         */
        confirmResult: (id, accepted) => api.post(`/challenges/${id}/confirm-result`, { accepted }),
        /**
         * @param {string} id
         * @param {FormData} formData
         */
        submitDispute: (id, formData) => api.post(`/challenges/${id}/dispute-with-proof`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }),
        /**
         * @param {string} id
         * @param {boolean} accepted
         */
        respondToAi: (id, accepted) => api.post(`/challenges/${id}/respond-ai-decision`, { accepted }),
        /**
         * @param {string} id
         */
        getFeedbackStatus: (id) => api.get(`/challenges/${id}/feedback-status`),

        /**
         * @param {string} id
         */
        sendFeedback: (id, data) => api.post(`/challenges/${id}/feedback`, data),
        /**
         * @param {string} id
         */
        canReview: (id) => api.get(`/reviews/can-review/${id}`),
        /**
         * @param {string} id
         */
        shareVictory: (id) => api.post(`/challenges/${id}/victory-share`),
    },
    dashboard: {
        /** 
         * From DashboardController: Returns gamertag, rank, wins, losses, 
         * win_rate, total_earnings, and balances.
         */
        getStats: () => api.get('/dashboard/stats'),
    },

    
    players: {
         /**
         * @param {string} id
         */
        getPlayer:(id)=>api.get(`/auth/${id}/stats`),
        /**
         * @param {Object} params
         */
        list: (params) => api.get('/players', { params }),
        /**
         * @param {string} id
         * @param {number} amount
         */
        backPlayer: (id, amount) => api.post(`/players/${id}/back`, { amount }),
        /**
         * @param {string} backingId
         */
        optOut: (backingId) => api.delete(`/backings/${backingId}`),
        getMyBackings: () => api.get('/me/backings'),
        getMyBackers: () => api.get('/me/backers'),
        /**
         * @param {string} playerId
         */
        startChat: (playerId) => api.post(`/users/${playerId}/start-chat`),
        getAvailablePlayers: (selectedRank) => api.get('/players/available', {
            params: selectedRank ? { rank: selectedRank } : {}
        }),
    
        searchPlayer: (searchQuery) => api.get(`/users/search?q=${encodeURIComponent(searchQuery)}`),

        
    },

    tournaments: {
        /**
         * @param {string} [status='all']
         */
        list: (status = 'all') => api.get('/tournaments', { params: { status } }),
        /**
         * @param {string} id
         */
        getDetails: (id) => api.get(`/tournaments/${id}`),
        /**
         * @param {string} id
         */
        join: (id) => api.post(`/tournaments/${id}/join`),
        /**
         * @param {string} tourneyId
         * @param {string} matchId
         * @param {string} winnerId
         */
        reportMatchResult: (tourneyId, matchId, winnerId) =>
            api.post(`/tournaments/${tourneyId}/matches/${matchId}/result`, { winner_id: winnerId }),
        /**
         * @param {string} tourneyId
         * @param {string} matchId
         * @param {boolean} accepted
         */
        confirmMatchResult: (tourneyId, matchId, accepted) =>
            api.post(`/tournaments/${tourneyId}/matches/${matchId}/confirm`, { accepted }),
    },

    kyc: {
        getStatus: () => api.get('/kyc/status'),
        /**
         * @param {FormData} formData
         */
        submitBasic: (formData) => api.post('/kyc/submit-basic', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }),
        /**
         * @param {FormData} formData
         */
        submitWithdrawal: (formData) => api.post('/kyc/submit-withdrawal', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }),
    },

    payments: {
        getRegion: () => api.get('/payment/detect-region'),
        getPackagesUSD: () => api.get('/payment/packages'),
        getPackagesNGN: () => api.get('/payment/packages-ngn'),
        /**
         * @param {string} packageId
         */
        createStripeSession: (data) => api.post('/payment/create-checkout-session', data),
        /**
         * @param {string} sessionId
         */
        pollStripeStatus: (sessionId) => api.get(`/payment/status/${sessionId}`),
        /**
         * @param {string} packageId
         */
        initializePaystack: (data) => api.post('/payment/paystack/initialize', data),
        /**
         * @param {string} reference
         */
        verifyPaystack: (reference) => api.get(`/payment/paystack/verify/${reference}`),
        getBanks: () => api.get('/payment/banks'),
        /**
         * @param {string} code
         * @param {string} num
         */
        verifyBankAccount: (code, num) => api.post(`/payment/paystack/verify-account?bank_code=${code}&account_number=${num}`),
    },

    messaging: {
        getConversations: () => api.get('/messages/conversations'),
        /**
         * @param {string} convId
         */
        getMessages: (convId) => api.get(`/messages/conversation/${convId}`),
        /**
         * @param {string} convId
         * @param {string} content
         */
        sendMessage: (convId, content) => api.post('/messages/send', { conversation_id: convId, content }),

        startConversation: (convId, content) => api.post('/messages/start-conversation', data),

    },

    match: {
        /**
         * @param {data} object
         */
        findMatch: (data) => api.post('/matchmaking/find', data),
        queue:(formData)=>api.get(`/matchmaking/queue?game=${formData.game}&entry_fee=${formData.entry_fee}`),
        cancel:(formData)=>api.delete(`/matchmaking/cancel?game=${formData.game}&entry_fee=${formData.entry_fee}`)
        
    },

    social: {
        /**
         * @param {number} [limit=100]
         */
        getLeaderboard: (limit = 100) => api.get('/seasons/leaderboard', { params: { limit } }),
        getCurrentSeason: () => api.get('/seasons/current'),
        getNotifications: () => api.get('/notifications'),
        /**
         * @param {string} id
         */
        markNotificationRead: (id) => api.patch(`/notifications/${id}/read`),
    },

    public: {
        getLandingChallenges: () => api.get('/public/challenges'),
        getLandingStats: () => api.get('/public/stats'),
    },

    notifications: {
        /** From NotificationsController */
        getNotifications: (limit = 20, offset = 0) => api.get('/notifications', { params: { limit, offset } }),
        /**
         * @param {string} id
         */
        markAsRead: (id) => api.patch(`/notifications/${id}/read`),
        markAllRead: () => api.patch('/notifications/read-all'),
    },

};